import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { createClient } from '@supabase/supabase-js';

let supabase = null;

function getSupabaseConfig() {
  return {
    url: import.meta.env.VITE_SUPABASE_URL || (typeof window !== 'undefined' && window.SUPABASE_URL) || '',
    key: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || (typeof window !== 'undefined' && window.SUPABASE_PUBLISHABLE_KEY) || ''
  };
}

export function getSupabase() {
  if (!supabase) {
    const { url, key } = getSupabaseConfig();
    if (url && key) {
      supabase = createClient(url, key);
      // Listen for auth state changes (critical for OAuth redirect flow)
      supabase.auth.onAuthStateChange((event, session) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
          // Import chat store dynamically to avoid circular deps
          import('./chat').then(mod => {
            const chatStore = mod.useChatStore();
            chatStore.loadFromServer();
          }).catch(() => {});
          // Check license agreement after OAuth sign-in
          fetch('/api/legal-agreement/status', {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          }).then(r => r.ok ? r.json() : null).then(data => {
            if (data && !data.accepted) {
              // Dynamically import auth store to set license gate
              import('./auth').then(mod => {
                const authStore = mod.useAuthStore();
                authStore.showLicenseGate = true;
                authStore.licenseAccepted = false;
              }).catch(() => {});
            }
          }).catch(() => {});
        }
      });
    }
  }
  return supabase;
}

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false);
  const userId = ref(null);
  const userEmail = ref(null);
  const isLoading = ref(false);
  const showAuthGate = ref(false);
  const showLicenseGate = ref(false);
  const licenseAccepted = ref(false);
  const authConfig = ref({ enabled: false, methods: [] });
  const error = ref(null);

  const displayName = computed(() => {
    if (userEmail.value) return userEmail.value;
    if (!userId.value) return '';
    return userId.value.length > 20
      ? userId.value.substring(0, 8) + '...'
      : userId.value;
  });

  async function checkAuthStatus() {
    try {
      // Always try Supabase session first if configured
      const sb = getSupabase();
      if (sb) {
        const { data: { session } } = await sb.auth.getSession();
        if (session) {
          isAuthenticated.value = true;
          userId.value = session.user.id;
          userEmail.value = session.user.email;
          showAuthGate.value = false;
          await checkLicenseAgreement();
          return;
        }
        // Supabase configured but no session — show auth gate so user can sign in
        showAuthGate.value = true;
        return;
      }

      // No Supabase config — check backend
      const response = await fetch('/api/auth/status');
      if (!response.ok) return;
      const data = await response.json();

      if (data.enabled) {
        isAuthenticated.value = data.authenticated;
        if (!data.authenticated) {
          showAuthGate.value = true;
        }
      } else {
        isAuthenticated.value = true;
      }
    } catch (e) {
      isAuthenticated.value = true;
    }
  }

  async function checkLicenseAgreement() {
    try {
      const sb = getSupabase();
      if (!sb) return;
      const { data: { session } } = await sb.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/legal-agreement/status', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!response.ok) return;
      const data = await response.json();

      if (data.accepted) {
        licenseAccepted.value = true;
        showLicenseGate.value = false;
      } else {
        licenseAccepted.value = false;
        showLicenseGate.value = true;
      }
    } catch (e) {
      // If check fails, allow access (don't block on network error)
      licenseAccepted.value = true;
    }
  }

  async function acceptLicense(fullName) {
    try {
      const sb = getSupabase();
      if (!sb) return false;
      const { data: { session } } = await sb.auth.getSession();
      if (!session) return false;

      const response = await fetch('/api/legal-agreement/accept', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ version: '1.0.0', fullName })
      });
      if (!response.ok) return false;

      licenseAccepted.value = true;
      showLicenseGate.value = false;
      return true;
    } catch (e) {
      return false;
    }
  }

  async function loginWithGoogle() {
    isLoading.value = true;
    error.value = null;
    try {
      const sb = getSupabase();
      if (!sb) {
        error.value = 'Authentication not configured';
        return false;
      }

      const { error: oauthError } = await sb.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (oauthError) {
        error.value = oauthError.message;
        return false;
      }
      return true;
    } catch (e) {
      error.value = e.message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function login(email, password) {
    isLoading.value = true;
    error.value = null;
    try {
      const sb = getSupabase();
      if (!sb) {
        error.value = 'Authentication not configured';
        return false;
      }

      const { data, error: signInError } = await sb.auth.signInWithPassword({
        email,
        password
      });

      if (signInError || !data.user) {
        error.value = signInError?.message || 'Login failed';
        return false;
      }

      isAuthenticated.value = true;
      userId.value = data.user.id;
      userEmail.value = data.user.email;
      showAuthGate.value = false;
      // Load persisted chat state after login
      import('./chat').then(mod => {
        const chatStore = mod.useChatStore();
        chatStore.loadFromServer();
      }).catch(() => {});
      await checkLicenseAgreement();
      return true;
    } catch (e) {
      error.value = e.message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function register(email, password) {
    isLoading.value = true;
    error.value = null;
    try {
      const sb = getSupabase();
      if (!sb) {
        error.value = 'Authentication not configured';
        return false;
      }

      const { data, error: signUpError } = await sb.auth.signUp({
        email,
        password
      });

      if (signUpError || !data.user) {
        error.value = signUpError?.message || 'Registration failed';
        return false;
      }

      isAuthenticated.value = true;
      userId.value = data.user.id;
      userEmail.value = data.user.email;
      showAuthGate.value = false;
      await checkLicenseAgreement();
      return true;
    } catch (e) {
      error.value = e.message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    const sb = getSupabase();
    if (sb) {
      await sb.auth.signOut();
    }
    isAuthenticated.value = false;
    userId.value = null;
    userEmail.value = null;
    licenseAccepted.value = false;
    showAuthGate.value = true;
    showLicenseGate.value = false;
  }

  function dismissAuthGate() {
    showAuthGate.value = false;
  }

  return {
    isAuthenticated,
    userId,
    userEmail,
    isLoading,
    showAuthGate,
    showLicenseGate,
    licenseAccepted,
    authConfig,
    error,
    displayName,
    checkAuthStatus,
    checkLicenseAgreement,
    acceptLicense,
    loginWithGoogle,
    login,
    register,
    logout,
    dismissAuthGate,
  };
});
